import React, { useState, useEffect } from 'react';
import { Save, Settings, RefreshCw, Clock, AlertTriangle, CheckCircle, Trash2, FileText } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

/**
 * Cron 配置页
 * 参考 Magento 2: cron_groups.xml 中的可配置参数
 */
const CronSettingsPage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('settings_cron', 'edit');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load configs
  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/cron');
      const json = await res.json();
      if (json.status === 'success') {
        setGroups(json.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // Update a single field
  const updateField = (groupIndex, field, value) => {
    const updated = [...groups];
    updated[groupIndex] = { ...updated[groupIndex], [field]: parseInt(value, 10) || 0 };
    setGroups(updated);
    setDirty(true);
  };

  // Save all groups
  const handleSave = async () => {
    setSaving(true);
    let hasError = false;
    for (const group of groups) {
      try {
        const res = await fetch('/api/settings/cron/edit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(group),
        });
        const json = await res.json();
        if (json.status !== 'success') {
          notify(`[${group.group_id}] ${json.message || 'Save failed'}`, 'error');
          hasError = true;
        }
      } catch (err) {
        notify(`[${group.group_id}] ${err.message}`, 'error');
        hasError = true;
      }
    }
    if (!hasError) {
      notify(t('settings.save_success'), 'success');
      setDirty(false);
      loadConfigs();
    }
    setSaving(false);
  };

  // Help text for each field
  const fieldMeta = {
    schedule_generate_every: {
      label: t('cron.schedule_generate_every') || 'Schedule generate every (min)',
      hint: t('cron.schedule_generate_every_hint') || 'How often to generate new schedules',
      icon: RefreshCw,
    },
    schedule_ahead_for: {
      label: t('cron.schedule_ahead_for') || 'Schedule ahead for (min)',
      hint: t('cron.schedule_ahead_for_hint') || 'How far ahead to generate schedules',
      icon: Clock,
    },
    schedule_lifetime: {
      label: t('cron.schedule_lifetime') || 'Schedule lifetime (min)',
      hint: t('cron.schedule_lifetime_hint') || 'Minutes before a scheduled job is considered missed',
      icon: AlertTriangle,
    },
    history_cleanup_every: {
      label: t('cron.history_cleanup_every') || 'History cleanup every (min)',
      hint: t('cron.history_cleanup_every_hint') || 'How often to clean up history records',
      icon: Trash2,
    },
    history_success_lifetime: {
      label: t('cron.history_success_lifetime') || 'Success history lifetime (min)',
      hint: t('cron.history_success_lifetime_hint') || 'Minutes to keep success records',
      icon: CheckCircle,
    },
    history_failure_lifetime: {
      label: t('cron.history_failure_lifetime') || 'Failure history lifetime (min)',
      hint: t('cron.history_failure_lifetime_hint') || 'Minutes to keep failure/missed records',
      icon: AlertTriangle,
    },
    use_separate_process: {
      label: t('cron.use_separate_process') || 'Use separate process',
      hint: t('cron.use_separate_process_hint') || 'Run this group in a separate PHP process (parallel execution)',
      icon: FileText,
    },
    parallel_execution_count: {
      label: t('cron.parallel_execution_count') || 'Max concurrent (per job)',
      hint: t('cron.parallel_execution_count_hint') || 'Maximum number of sub-processes running concurrently within a single job',
      icon: FileText,
    },
    max_batch: {
      label: t('cron.max_batch') || 'Max batch size',
      hint: t('cron.max_batch_hint') || 'Maximum number of records processed per job run (0 = unlimited)',
      icon: FileText,
    },
  };

  const fieldKeys = Object.keys(fieldMeta);

  return (
    <div className="space-y-6">
      {/* Title + Save */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('cron.title')}</h1>
        {canEdit && (
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className={`bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${
              saving || !dirty ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t('common.processing')}
              </span>
            ) : (
              <><Save size={18} /> {t('common.save')}</>
            )}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-12 text-center">
          <Settings size={48} className="mx-auto mb-4 text-slate-300 dark:text-slate-600" />
          <p className="text-slate-500 dark:text-slate-400">{t('cron.no_groups_configured')}</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">
            {t('cron.no_groups_hint')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group, gIdx) => (
            <div key={group.group_id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              {/* Group Header */}
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
                <Settings size={20} className="text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                  {t('cron.group_label')}: <span className="font-mono text-blue-600 dark:text-blue-400">{group.group_id}</span>
                </h2>
              </div>

              {/* Fields */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {fieldKeys.map(field => {
                    const meta = fieldMeta[field];
                    const Icon = meta.icon;
                    const val = group[field];
                    const isBoolField = field === 'use_separate_process';

                    return (
                      <div key={field} className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-400">
                          <Icon size={14} className="text-slate-400" />
                          {meta.label}
                        </label>

                        {isBoolField ? (
                          <select
                            value={val}
                            onChange={e => updateField(gIdx, field, e.target.value)}
                            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-transparent dark:text-white"
                          >
                            <option value="0">{t('common.no')}</option>
                            <option value="1">{t('common.yes')}</option>
                          </select>
                        ) : (
                          <input
                            type="number"
                            min="1"
                            value={val}
                            onChange={e => updateField(gIdx, field, e.target.value)}
                            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                          />
                        )}

                        <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">
                          {meta.hint}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CronSettingsPage;
